import type { SovBrandRow } from '@/types/shareOfVoice';

const MARKET_LABELS: Record<string, string> = {
  us: 'United States',
  uk: 'United Kingdom',
  de: 'Germany',
  in: 'India',
  ca: 'Canada',
  fr: 'France',
  it: 'Italy',
  es: 'Spain',
  mx: 'Mexico',
  jp: 'Japan',
};

export function marketplaceLabel(code: string | null | undefined): string {
  if (!code) return 'Unsupported marketplace';
  return MARKET_LABELS[code] ?? code.toUpperCase();
}

export function formatSov(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return `${(value * 100).toFixed(1)}%`;
}

export function formatDeltaPp(value: number | null | undefined): string | null {
  if (value == null || Number.isNaN(value)) return null;
  const arrow = value > 0.05 ? '↑' : value < -0.05 ? '↓' : '→';
  return `${arrow} ${Math.abs(value).toFixed(1)} pp`;
}

export function formatPosition(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return value.toFixed(1);
}

export function formatPositionDelta(value: number | null | undefined): string | null {
  if (value == null || Number.isNaN(value)) return null;
  const arrow = value > 0.05 ? '↑' : value < -0.05 ? '↓' : '→';
  return `${arrow} ${Math.abs(value).toFixed(1)}`;
}

export function formatSearchVolume(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat(undefined, {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
}

export function formatBid(value: number | null | undefined, currency: string | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  const code = currency || 'USD';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: code,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${value.toFixed(2)} ${code}`;
  }
}

export function formatCount(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(value);
}

export function splitKeywordInput(raw: string): string[] {
  return raw
    .split(/[\n,]/)
    .map((part) => part.trim())
    .filter(Boolean);
}

export type SovBarRow = {
  label: string;
  value: number;
  own: boolean;
};

export function sovBarRows(brands: SovBrandRow[], metric: 'organic' | 'paid', limit = 8): SovBarRow[] {
  const valueOf = (brand: SovBrandRow) => (metric === 'organic' ? brand.organicSov : brand.paidSov);
  const own = brands.filter((brand) => brand.isOwnBrand);
  const ownValue = own.reduce((sum, brand) => sum + (valueOf(brand) ?? 0), 0);
  const ranked = brands
    .filter((brand) => !brand.isOwnBrand && valueOf(brand) != null)
    .sort((left, right) => (valueOf(right) ?? 0) - (valueOf(left) ?? 0));
  const shown = ranked.slice(0, limit);
  const shownSum = shown.reduce((sum, brand) => sum + (valueOf(brand) ?? 0), 0);
  const remainder = Math.max(0, 1 - (own.length ? ownValue : 0) - shownSum);
  const rows: SovBarRow[] = [];
  if (own.length) rows.push({ label: 'Your brand', value: ownValue, own: true });
  for (const brand of shown) {
    rows.push({ label: brand.brandName, value: valueOf(brand) ?? 0, own: false });
  }
  if (remainder >= 0.005) rows.push({ label: 'Others', value: remainder, own: false });
  return rows;
}
